import { useState } from 'react';
import clsx from 'clsx';
import { MaterialSymbolIcon } from '@/components/material-symbol-link';
import { MAX_FILE_COUNT, MAX_FILE_SIZE } from '@/util/constants';
import { useCreateEncountersFiles } from '@/api/create-file';
import { useEncounter } from '@/contexts/encounter-context';
import { getGetEncountersFilesQueryKey, useGetEncountersFiles } from '@/api/get-files';
import { useDeleteEncountersFiles } from '@/api/delete-file';
import { useTranslation } from '@/contexts/i18n-context';
import { Loader } from '@/components/loaders/loader';
import { DropZone } from '@/components/uploads/drop-zone';
import { kebabToCamelCase } from '@/util/helpers';
import { useQueryClient } from '@tanstack/react-query';
import { RegularButtonProps } from '@/components/button';
import styles from './file-upload.module.css';
import dropZoneStyles from '../drop-zone.module.css';
import { FileInput } from '../file-input';

interface FileUploadProps {
  label: string;
  description?: string | null;
}

export function FileUpload({ label, description }: FileUploadProps) {
  const { encounter } = useEncounter();
  const { data: files, isPending } = useGetEncountersFiles(encounter.id);

  if (isPending) {
    return <Loader type="small" />;
  }

  return (
    <div>
      <div className={styles.titleContainer}>
        <h3 className={clsx('body-2', styles.componentTitle)}>{label}</h3>
        {description && <p className={clsx('body-3', styles.subtitle)}>{description}</p>}
      </div>
      <FileDropZone
        initialFiles={
          files?.files.map((file) => ({
            id: file.id,
            name: file.description,
            tempId: file.path,
            status: 'uploaded',
          })) ?? []
        }
      />
    </div>
  );
}

interface FileDropZoneProps {
  initialFiles: FileItem[];
}

interface FileItem {
  id?: number;
  name: string;
  tempId: string;
  status: 'uploading' | 'uploaded' | 'error' | 'error-large';
}

const translationBase = 'visit.fileUpload';

export function FileDropZone({ initialFiles }: FileDropZoneProps) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<FileItem[]>(initialFiles);
  const { encounter } = useEncounter();
  const { mutateAsync: uploadFile } = useCreateEncountersFiles();
  const { mutateAsync: deleteFile } = useDeleteEncountersFiles();
  const [deletingId, setDeletingId] = useState<string>();
  const queryClient = useQueryClient();

  const invalidateFiles = async () => {
    await queryClient.invalidateQueries({ queryKey: getGetEncountersFilesQueryKey(encounter.id) });
  };

  const handleFilesSelected = async (files: File[]) => {
    const filesToUpload = files.slice(0, MAX_FILE_COUNT - files.length);

    setFiles((prev) => [
      ...prev,
      ...filesToUpload.map((file) => ({
        name: file.name,
        tempId: Date.now().toString() + file.name,
        status: file.size > MAX_FILE_SIZE ? ('error-large' as const) : ('uploading' as const),
      })),
    ]);

    const results = await Promise.all(
      filesToUpload
        .filter((file) => file.size <= MAX_FILE_SIZE)
        .map(async (file) => {
          try {
            const result = await uploadFile({
              encounterId: encounter.id,
              data: {
                file,
              },
            });

            return {
              fileId: result.fileId,
              name: file.name,
              status: 'uploaded' as const,
            };
          } catch {
            return {
              fileId: undefined,
              name: file.name,
              status: 'error' as const,
            };
          }
        }),
    );

    await invalidateFiles();

    setFiles((prev) =>
      prev.map((file) => {
        const result = results.find((r) => r.name === file.name);

        if (result) {
          return {
            id: result.fileId,
            name: file.name,
            tempId: file.tempId,
            status: result.status,
          };
        }

        return file;
      }),
    );
  };

  const handleDelete = (tempId: string, fileId?: number) => {
    if (fileId) {
      setDeletingId(tempId);
      deleteFile(
        { encounterId: encounter.id, fileId },
        {
          onSuccess: async () => {
            setFiles((prev) => prev.filter((file) => file.tempId !== tempId));
            setDeletingId(undefined);
            await invalidateFiles();
          },
          onError: () => setDeletingId(undefined),
        },
      );
    } else {
      setFiles((prev) => prev.filter((file) => file.tempId !== tempId));
    }
  };

  const stopEventPropagation = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    stopEventPropagation(e);
    handleFilesSelected(Array.from(e.dataTransfer.files));
  };

  const hasFiles = files.length > 0;

  const buttonProps: RegularButtonProps = {
    leftIcon: 'add',
    size: 'S',

    ...(hasFiles
      ? {
          variant: 'none',
          className: dropZoneStyles.uploadMoreButton,
          children: t('common.uploadMore'),
          onDragOver: stopEventPropagation,
          onDrop: handleDrop,
        }
      : {
          variant: 'outlined',
          className: dropZoneStyles.uploadButton,
          children: t(`${translationBase}.uploadFilesButton`),
        }),
  };

  return (
    <DropZone
      onChange={handleFilesSelected}
      title={t(`${translationBase}.title`)}
      description={t(`${translationBase}.description`)}
      hasFiles={hasFiles}
      showUploadMoreButton={files.length < MAX_FILE_COUNT}
      inputField={
        <FileInput
          onFilesSelected={handleFilesSelected}
          acceptType="files"
          multiple
          buttonProps={buttonProps}
        />
      }
    >
      {files.map((file) => (
        <FileItem
          key={file.tempId}
          file={file}
          isDeleting={deletingId === file.tempId}
          onDelete={() => handleDelete(file.tempId, file.id)}
          deleteDisabled={!!deletingId}
        />
      ))}
    </DropZone>
  );
}

interface FileItemProps {
  file: FileItem;
  onDelete?: () => void;
  className?: string;
  isDeleting?: boolean;
  deleteDisabled?: boolean;
  truncateName?: boolean;
}

export function FileItem({
  file,
  onDelete,
  className,
  isDeleting = false,
  deleteDisabled = false,
  truncateName = false,
}: FileItemProps) {
  const { t } = useTranslation();

  return (
    <div className={clsx(styles.item, className)}>
      <MaterialSymbolIcon name="description" className={styles.icon} />
      <div className={clsx(styles.fileDetails, truncateName && styles.truncateName)}>
        {isDeleting ? (
          <Loader type="small" />
        ) : (
          <>
            <p className={clsx('body-3', styles.name, truncateName && styles.truncatedName)}>
              {file.name}
            </p>
            {file.status !== 'uploaded' && (
              <p
                className={clsx(
                  'body-3',
                  styles.status,
                  file.status.startsWith('error') && styles.error,
                )}
              >
                {t(`${translationBase}.status.${kebabToCamelCase(file.status)}`)}
              </p>
            )}
          </>
        )}
      </div>
      {onDelete && (
        <MaterialSymbolIcon
          name="close"
          ariaLabel={t(`${translationBase}.deleteFile`)}
          onClick={onDelete}
          disabled={file.status === 'uploading' || deleteDisabled}
          className={styles.deleteIcon}
        />
      )}
    </div>
  );
}
